import React, { useEffect, useRef, useState } from "react";
import { attachAnalyser } from "@/lib/audioGraph";

/**
 * TalkingHead — CRT-monitor-faced robot that replaces the orb when
 * orbStyle === "crt_head".
 *
 * Features:
 *  • Audio-reactive mouth driven by Web Audio API AnalyserNode (when an
 *    HTMLAudioElement is provided via the audioEl prop). Falls back to a
 *    soft sine wobble when speaking is true but no audio element is wired
 *    (browser TTS path).
 *  • Idle blinks every ~3–6s when not speaking.
 *  • Subtle head bob/glance so the robot looks alive even silent.
 *  • Whole body is the click target — replaces the orb tap button.
 *
 * Owner brief: "make the second screenshot the head … move around to make
 * it look alive, make it blink and smile, in the mouth go with the voices
 * as it's speaking so it looks like that robot is saying what's coming
 * out the speaker".
 */
export default function TalkingHead({
  active = false,
  listening = false,
  speaking = false,
  audioEl = null,
  onTap,
  agentName = "JARVIS",
  palette,        // optional { primary, secondary, glow, eyeShape, mouthRows }
}) {
  const [amp, setAmp] = useState(0);          // 0..1 mouth-open driver
  const [blink, setBlink] = useState(false);   // brief eye-close
  const [glance, setGlance] = useState({ x: 0, y: 0 });
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const srcNodeRef = useRef(null);
  const rafRef = useRef(0);
  const lastAudioElRef = useRef(null);

  // ─── BLINK ──────────────────────────────────────────────────────────
  useEffect(() => {
    let t;
    const tick = () => {
      setBlink(true);
      setTimeout(() => setBlink(false), 120);
      t = setTimeout(tick, 2800 + Math.random() * 3200);
    };
    t = setTimeout(tick, 2000);
    return () => clearTimeout(t);
  }, []);

  // ─── GLANCE (idle head movement) ────────────────────────────────────
  useEffect(() => {
    if (speaking || listening) {
      setGlance({ x: 0, y: 0 });
      return;
    }
    let t;
    const tick = () => {
      setGlance({
        x: (Math.random() - 0.5) * 14,
        y: (Math.random() - 0.5) * 6,
      });
      t = setTimeout(tick, 1800 + Math.random() * 1800);
    };
    t = setTimeout(tick, 1200);
    return () => clearTimeout(t);
  }, [speaking, listening]);

  // ─── AUDIO-REACTIVE MOUTH (via Web Audio AnalyserNode) ───────────────
  useEffect(() => {
    // If we get a fresh audio element, wire up an analyser to its stream
    // so we can pull real-time amplitude → drive mouth-open ratio.
    if (audioEl && audioEl !== lastAudioElRef.current) {
      lastAudioElRef.current = audioEl;
      // Use the shared audioGraph so we never double-create a source on
      // the same <audio> element (which silently kills downstream TTS).
      analyserRef.current = attachAnalyser(audioEl, { fftSize: 256 });
    }

    // Animation loop: read analyser → set mouth amplitude
    const tick = () => {
      const analyser = analyserRef.current;
      if (analyser && speaking) {
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(buf);
        // RMS-ish amplitude
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const next = Math.min(1, rms * 3.2);
        setAmp((prev) => prev * 0.4 + next * 0.6);
      } else if (speaking) {
        // No analyser available — fake amplitude with sine wobble
        const t = performance.now() / 130;
        const fake = (Math.sin(t) * 0.4 + Math.sin(t * 1.7) * 0.3 + 0.5) * 0.6;
        setAmp(Math.max(0, fake));
      } else {
        setAmp((prev) => prev * 0.65);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [audioEl, speaking]);

  // ─── COMPUTED ────────────────────────────────────────────────────────
  const mouthOpenPx = 6 + amp * 38;            // 6..44 px tall
  const headTiltDeg = glance.x * 0.4 + (speaking ? Math.sin(performance.now() / 250) * 1.2 : 0);
  const pal = palette || {};
  const primaryColor = pal.primary || (listening ? "#ff4d4d" : speaking ? "#7df9ff" : "#3ed1ff");
  const secondaryColor = pal.secondary || "rgba(125,170,255,0.55)";
  const bezelFill = pal.bezel || "rgba(10,20,42,0.92)";
  const eyeColor = listening ? "#ff4d4d" : primaryColor;
  const glowColor = pal.glow || (listening
    ? "rgba(255,77,77,0.65)"
    : speaking
    ? "rgba(125,249,255,0.65)"
    : "rgba(62,209,255,0.45)");
  const glow = `0 0 38px ${glowColor}`;

  // Mouth is the centerpiece — when speaking it morphs from a smile arc
  // into a wider "O" the louder the audio.
  // We render two rows of LEDs for the mouth: top static, bottom drops
  // down based on amp to create the "open" effect.

  return (
    <div
      onClick={onTap}
      data-testid="talking-head"
      className="absolute inset-0 flex items-center justify-center cursor-pointer select-none"
      style={{
        transform: `translate(${glance.x}px, ${glance.y}px) rotate(${headTiltDeg}deg)`,
        transition: "transform 600ms cubic-bezier(.2,.7,.3,1)",
      }}
    >
      {/* Body silhouette behind the CRT (subtle hood) */}
      <svg
        viewBox="0 0 320 380"
        width="100%"
        height="100%"
        style={{
          filter: `drop-shadow(${glow})`,
          maxWidth: 520,
          maxHeight: 560,
        }}
      >
        {/* hood/shoulders */}
        <path
          d="M 40 360 Q 40 250 110 240 Q 160 220 210 240 Q 280 250 280 360 Z"
          fill="rgba(8,18,40,0.85)"
          stroke="rgba(62,209,255,0.35)"
          strokeWidth="1.2"
        />
        {/* CRT bezel — outer */}
        <rect
          x="58" y="60" width="204" height="170" rx="10"
          fill={bezelFill}
          stroke={secondaryColor}
          strokeWidth="3"
        />
        {/* CRT top vents */}
        <g opacity="0.4" stroke="rgba(125,170,255,0.7)" strokeWidth="1">
          {Array.from({ length: 14 }).map((_, i) => (
            <line key={i} x1={70 + i * 14} y1={45} x2={70 + i * 14} y2={55} />
          ))}
        </g>
        {/* CRT screen — inner */}
        <rect
          x="76" y="80" width="168" height="132" rx="4"
          fill="#031229"
          stroke="rgba(62,209,255,0.5)"
          strokeWidth="1.5"
        />
        {/* faint scanlines */}
        <g opacity="0.25" stroke="rgba(62,209,255,0.4)" strokeWidth="0.6">
          {Array.from({ length: 22 }).map((_, i) => (
            <line key={i} x1={78} y1={85 + i * 6} x2={242} y2={85 + i * 6} />
          ))}
        </g>

        {/* EYES — LED pixel-blocks */}
        <g>
          {[120, 200].map((cx) => (
            <g key={cx}>
              {/* upper row */}
              <rect x={cx - 12} y={blink ? 120 : 108} width="9" height={blink ? 4 : 9} fill={eyeColor} opacity="0.95" />
              <rect x={cx + 3}  y={blink ? 120 : 108} width="9" height={blink ? 4 : 9} fill={eyeColor} opacity="0.95" />
              {/* lower row (hidden when blinking) */}
              {!blink && (
                <>
                  <rect x={cx - 12} y={120} width="9" height="9" fill={eyeColor} opacity="0.75" />
                  <rect x={cx + 3}  y={120} width="9" height="9" fill={eyeColor} opacity="0.75" />
                </>
              )}
            </g>
          ))}
        </g>

        {/* MOUTH — LED grid, height driven by amp. When closed (amp≈0) it
            shows a flat 1-row smile; when open it grows into a tall "O". */}
        <g>
          {(() => {
            // mouth bounding box
            const left = 116, right = 204;
            const baseTop = 168;
            const rows = Math.max(1, Math.round(amp * 5) + 1); // 1..6 rows
            const cellH = 6;
            const cellW = 10;
            const cols = Math.floor((right - left) / cellW);
            const els = [];
            for (let r = 0; r < rows; r++) {
              for (let c = 0; c < cols; c++) {
                // Smile shape: corners curl up at rest, square when open loud
                const yShift = (amp < 0.2)
                  ? Math.abs((c - cols / 2) / cols) * 6   // smile arc
                  : 0;
                els.push(
                  <rect
                    key={`m${r}-${c}`}
                    x={left + c * cellW + 1}
                    y={baseTop + r * cellH + yShift}
                    width={cellW - 2}
                    height={cellH - 1}
                    fill={eyeColor}
                    opacity={r === 0 ? 0.95 : 0.85 - r * 0.1}
                  />
                );
              }
            }
            return els;
          })()}
        </g>

        {/* Listening pulse ring around the CRT */}
        {listening && (
          <rect
            x="58" y="60" width="204" height="170" rx="10"
            fill="none"
            stroke="rgba(255,77,77,0.6)"
            strokeWidth="3"
          >
            <animate attributeName="stroke-opacity" values="0.2;0.9;0.2" dur="1.2s" repeatCount="indefinite" />
          </rect>
        )}

        {/* Agent label — small CRT caption */}
        <text
          x="160" y="252"
          textAnchor="middle"
          fontFamily="Share Tech Mono, monospace"
          fontSize="12"
          letterSpacing="3"
          fill="rgba(125,170,255,0.7)"
        >
          {(agentName || "").toUpperCase()}
        </text>
      </svg>
    </div>
  );
}
